import { useTheme } from '@mui/material';
import { clamp } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { CENTER, DEFAULT_DIAL_OPTIONS, SIZE } from './constants';
import StyledDial from './styled';
import type { DialOptions, DialProps } from './types';

const DEFAULT_OPTIONS: DialOptions = {
  ...DEFAULT_DIAL_OPTIONS,
  mouseWheelStep: 1,
  mouseMoveStep: 1,
};

const Dial = ({ value, onChange, options }: DialProps) => {
  const { mouseMoveStep, mouseWheelStep, min, max, angleOffset, angleRange } = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  const theme = useTheme();
  const ref = useRef<SVGSVGElement>(null);
  const valueRef = useRef<number>(value);

  const applyDelta = useCallback(
    (delta: number) => {
      const newValue = valueRef.current + delta;
      valueRef.current = clamp(newValue, min, max);
      onChange(valueRef.current);
    },
    [valueRef, onChange, min, max]
  );

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    let prevPosition = 0;

    const handleMouseMove = (event: MouseEvent) => {
      event.preventDefault();

      const delta = prevPosition - event.clientY;
      applyDelta(delta * mouseMoveStep);

      prevPosition = event.clientY;
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    const handleMouseDown = (event: MouseEvent) => {
      event.preventDefault();
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      prevPosition = event.clientY;
    };

    element.addEventListener('mousedown', handleMouseDown);

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      applyDelta(event.deltaY * mouseWheelStep);
    };
    element.addEventListener('wheel', handleWheel);

    const handleTouchStart = (event: TouchEvent) => {
      event.preventDefault();
      const touches = event.changedTouches;
      const first = touches[0];
      prevPosition = first.clientY;
    };
    const handleTouchMove = (event: TouchEvent) => {
      event.preventDefault();

      const touches = event.changedTouches;
      const first = touches[0];
      const delta = prevPosition - first.clientY;
      applyDelta(delta * mouseMoveStep);

      prevPosition = first.clientY;
    };
    element.addEventListener('touchstart', handleTouchStart);
    element.addEventListener('touchmove', handleTouchMove);

    return () => {
      element?.removeEventListener('mousedown', handleMouseDown);
      element?.removeEventListener('wheel', handleWheel);

      element?.removeEventListener('touchstart', handleTouchStart);
      element?.removeEventListener('touchmove', handleTouchMove);
    };
  }, [applyDelta, min, max, mouseMoveStep, mouseWheelStep, ref]);

  return (
    <StyledDial
      ref={ref}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      style={{
        transform: `rotate(${(value / max) * angleRange + angleOffset}deg)`,
      }}
    >
      <line
        x1={CENTER}
        y1="40"
        x2={CENTER}
        y2="12"
        strokeWidth="3"
        stroke={theme.palette.grey[900]}
      />
    </StyledDial>
  );
};

export default Dial;
